// The two rules the `user_preferences` row turns on, kept pure so they can be
// tested without a renderer.
//
// The row holds a single JSON `data` blob shared by every preference in the
// app — the tasks layout, the shortcut overrides, the peek-panel dock side. A
// write sends the whole blob, not a patch of one key, so whoever writes last
// decides the value of every key. That makes the merge rule below load-bearing
// rather than a convenience: a writer that builds its payload from a stale copy
// silently reverts every key another writer has touched since.

export type PrefsData = Record<string, unknown>;

/**
 * The blob to send for a write of `key`, merged onto the latest known value.
 * `prev` must be the newest blob the app has seen, not the copy a component
 * captured when it rendered.
 */
export function mergePref(prev: PrefsData, key: string, value: unknown): PrefsData {
  return { ...prev, [key]: value };
}

/**
 * True once a login has put an access token in storage. Preferences are only
 * readable by their owner, so asking for them unauthenticated returns a 403
 * that is indistinguishable from "this collection was never migrated" — and
 * that misreading drops the app into its localStorage fallback for the rest of
 * the session. Checking first is what keeps the two apart.
 */
export function hasAuthToken(raw: string | null): boolean {
  if (!raw) return false;
  try {
    return Boolean(JSON.parse(raw)?.access_token);
  } catch {
    return false;
  }
}
